'use client';

import { useLocale, useTranslations } from 'next-intl';
import { OptimizedImage } from '@/components/images/optimized-image';
import { IdentityBeam, ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import { ReactionVoteButtons } from '@/components/shared/reaction-vote-buttons';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { formatCountFor } from '@/lib/format/format-count';
import { formatRelativeTime } from '@/lib/format-relative-time';
import type { TopBackerSummary } from '@/lib/launchpad/derive-fake-fields';
import {
  FEED_CARD_HOVER_BACKGROUND,
  FEED_CARD_OUTLINE_INTERACTION,
  FEED_CARD_RESTING_OUTLINE,
} from '@/lib/ui/feed-card-chrome';
import { cn } from '@/lib/ui/utils';
import { BackerAvatarPile } from './proposal-backer-stack';
import { ProposalFundingStatus } from './proposal-funding-status';
import { ProposalOutcomeCount } from './proposal-outcome-count';
import { ProposalProposerCell } from './proposal-proposer-cell';
import type { ProposalScore } from './proposal-score-badge';
import { ProposalScoreBadge } from './proposal-score-badge';
import type { ProposalFeedEventVariant, ProposalListItem } from './types';
import { useProposalVoteModel } from './use-proposal-vote-model';

/** Kept for backwards compatibility — prefer {@link FEED_CARD_RESTING_OUTLINE}. */
export const PROPOSAL_FEED_CARD_RESTING_OUTLINE: typeof FEED_CARD_RESTING_OUTLINE =
  FEED_CARD_RESTING_OUTLINE;

/** Kept for backwards compatibility — prefer {@link FEED_CARD_OUTLINE_INTERACTION}. */
export const PROPOSAL_FEED_CARD_OUTLINE_INTERACTION: typeof FEED_CARD_OUTLINE_INTERACTION =
  FEED_CARD_OUTLINE_INTERACTION;

export type ProposalFeedCardVariant = 'mobile' | 'tablet' | 'compact';

export interface ProposalFeedCardProps {
  proposal: ProposalListItem;
  proposalScore: ProposalScore;
  topBackers: TopBackerSummary[];
  priorityImage?: boolean;
  animateValuesOnMount?: boolean;
  /** Event-page presentation: hides launchpad reactions and supplies the event's category glyph. */
  eventVariant?: ProposalFeedEventVariant;
  variant: ProposalFeedCardVariant;
  /** Keep the selected card visible inside fixed-width visual playground stages. */
  standalone?: boolean;
  /** Hover-prefetch callback — warms the v2 SDK cache for the destination
   *  proposal-detail page. Idempotent. Skipped for graduated proposals
   *  (those route to /markets/<pubkey> via a different SDK hook). */
  onHoverPrefetch?: (proposalPubkey: string) => void;
  /**
   * Optional override for the card link's `target`. Used by the similar-
   * markets section on desktop to open neighbour proposals in a new tab.
   * When omitted, the card opens in the same tab.
   */
  target?: string | undefined;
  /** Optional override for the card link's `rel`. Pair with a `_blank` target. */
  rel?: string | undefined;
}

function cardVisibilityClasses(variant: ProposalFeedCardVariant, standalone: boolean): string {
  if (standalone) return 'flex flex-col gap-3 min-h-0 h-full';
  switch (variant) {
    case 'mobile':
      return 'md:hidden flex flex-col gap-3';
    case 'tablet':
      return 'hidden md:flex lg:hidden flex-col gap-3 min-h-0 h-full';
    case 'compact':
      return 'flex flex-col gap-3 min-h-0 h-full';
    default: {
      const _exhaustive: never = variant;
      return _exhaustive;
    }
  }
}

function cardRootTestId(variant: ProposalFeedCardVariant): string {
  switch (variant) {
    case 'mobile':
      return 'proposal-feed-row-mobile-root';
    case 'tablet':
      return 'proposal-feed-card-tablet-root';
    case 'compact':
      return 'proposal-feed-card-compact-root';
    default: {
      const _exhaustive: never = variant;
      return _exhaustive;
    }
  }
}

function titleTestId(variant: ProposalFeedCardVariant): string {
  switch (variant) {
    case 'mobile':
      return 'proposal-feed-row-mobile-title';
    case 'tablet':
      return 'proposal-feed-card-title';
    case 'compact':
      return 'proposal-feed-card-compact-title';
    default: {
      const _exhaustive: never = variant;
      return _exhaustive;
    }
  }
}

function headerTestId(variant: ProposalFeedCardVariant): string {
  switch (variant) {
    case 'mobile':
      return 'proposal-feed-row-mobile-header';
    case 'tablet':
      return 'proposal-feed-card-header';
    case 'compact':
      return 'proposal-feed-card-compact-header';
    default: {
      const _exhaustive: never = variant;
      return _exhaustive;
    }
  }
}

function metaTestId(variant: ProposalFeedCardVariant): string {
  switch (variant) {
    case 'mobile':
      return 'proposal-feed-row-mobile-meta';
    case 'tablet':
      return 'proposal-feed-card-meta';
    case 'compact':
      return 'proposal-feed-card-compact-meta';
    default: {
      const _exhaustive: never = variant;
      return _exhaustive;
    }
  }
}

function footerTestId(variant: ProposalFeedCardVariant): string {
  switch (variant) {
    case 'mobile':
      return 'proposal-feed-row-mobile-footer';
    case 'tablet':
      return 'proposal-feed-card-footer';
    case 'compact':
      return 'proposal-feed-card-compact-footer';
    default: {
      const _exhaustive: never = variant;
      return _exhaustive;
    }
  }
}

/**
 * Shared launchpad proposal card — mobile stack (&lt; md) or tablet grid item (md–lg, two columns via parent grid).
 * Desktop (lg+) uses {@link ProposalFeedRow} instead.
 */
export function ProposalFeedCard({
  proposal,
  proposalScore,
  topBackers,
  priorityImage = false,
  animateValuesOnMount = true,
  eventVariant,
  variant,
  standalone = false,
  onHoverPrefetch,
  target,
  rel,
}: ProposalFeedCardProps): React.ReactNode {
  const tTable = useTranslations('launchpad.feed.table');
  const tTimeIntl = useTranslations('time');
  const locale = useLocale();
  const formatCount = formatCountFor(locale);
  const proposalVotingEnabled = useFeatureGate('proposal_voting') === true;
  const showVoting = proposalVotingEnabled && eventVariant === undefined;
  const voteModel = useProposalVoteModel(proposal.pubkey, {
    upvotes: proposal.upvotes ?? 0,
    downvotes: proposal.downvotes ?? 0,
    myVote: proposal.myVote ?? null,
  });

  const isGraduated = proposal.graduatedMarketPubkey !== null;
  const href = isGraduated
    ? `/markets/${proposal.graduatedMarketPubkey}`
    : `/proposals/${proposal.pubkey}`;
  function handleHover(): void {
    if (!isGraduated) {
      onHoverPrefetch?.(proposal.pubkey);
    }
  }
  // Full route prefetch on intent. handleHover is a no-op for graduated rows
  // (it warms the proposal cache, not the markets cache they route to).
  const intent = useIntentRoutePrefetch(href, handleHover);
  const createdAtLabel =
    variant === 'compact'
      ? ''
      : formatRelativeTime(
          proposal.createdAt,
          (key: string, params?: Record<string, number>): string =>
            tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params),
          { locale },
        );

  return (
    <IdentityBeam marketType={proposal.marketType}>
      <div
        className={cn(
          cardVisibilityClasses(variant, standalone),
          'relative rounded-card bg-background-primary px-4 py-4',
          FEED_CARD_HOVER_BACKGROUND,
          PROPOSAL_FEED_CARD_RESTING_OUTLINE,
          PROPOSAL_FEED_CARD_OUTLINE_INTERACTION,
        )}
        data-testid={cardRootTestId(variant)}
      >
        <Link
          href={href}
          prefetch={false}
          target={target}
          rel={rel}
          {...intent}
          aria-label={proposal.title}
          className="absolute inset-0 rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-border-primary"
        />
        <div className="flex items-center gap-3 min-w-0" data-testid={headerTestId(variant)}>
          <ThumbWithOrigin marketType={proposal.marketType}>
            <OptimizedImage
              variant="thumbnail"
              entity={{
                kind: 'market',
                pubkey: proposal.pubkey,
                category: String(proposal.category ?? '').toLowerCase(),
                coverImageUrl:
                  proposal.coverImageUrl !== null && proposal.coverImageUrl !== ''
                    ? proposal.coverImageUrl
                    : null,
                nsfw: (proposal as { coverImageNsfw?: boolean | null }).coverImageNsfw === true,
              }}
              alt=""
              size="size-12"
              rounded={variant === 'mobile' ? 'rounded-overlay-item' : 'rounded-control'}
              // ALLOW-NO-PREVIEW: rendered inside a navigable card link; nested click targets would steal navigation
              preview={false}
              priority={priorityImage}
            />
          </ThumbWithOrigin>
          {variant === 'compact' ? (
            <h3
              className="line-clamp-2 flex-1 min-w-0 text-body font-medium text-foreground-primary"
              data-testid={titleTestId(variant)}
              title={proposal.title}
            >
              {proposal.title}
            </h3>
          ) : (
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <h3
                className="line-clamp-2 min-w-0 text-body font-medium text-foreground-primary"
                data-testid={titleTestId(variant)}
                title={proposal.title}
              >
                {proposal.title}
              </h3>
              {createdAtLabel !== '' ? (
                <time
                  className="text-caption text-foreground-tertiary"
                  dateTime={proposal.createdAt}
                >
                  {createdAtLabel}
                </time>
              ) : null}
            </div>
          )}
          <div className="shrink-0">
            <ProposalScoreBadge score={proposalScore} />
          </div>
        </div>

        <ProposalFundingStatus
          totalFunded={proposal.totalFunded}
          creationThreshold={proposal.creationThreshold}
          animateValuesOnMount={animateValuesOnMount}
        />

        <div className="flex items-center gap-4 min-w-0" data-testid={metaTestId(variant)}>
          <div className="min-w-0 flex-1">
            <ProposalProposerCell
              proposer={{
                walletAddress: proposal.creator,
                displayName: proposal.proposerDisplayName,
                handle: proposal.proposerHandle,
                avatarUrl: proposal.proposerAvatarUrl,
                avatarNsfw: proposal.proposerAvatarNsfw,
                avatarPreferences: proposal.proposerAvatarPreferences,
                connectedProviders: proposal.proposerConnectedProviders,
              }}
              disableNavigation
            />
          </div>
          <div
            className="flex items-center gap-2 shrink-0"
            data-testid="proposal-feed-card-backers-cell"
          >
            <div className="flex flex-col items-end leading-none">
              <AnimatedNumber
                value={proposal.backerCount}
                format={formatCount}
                animateOnMount={animateValuesOnMount}
                className="text-body font-medium text-foreground-primary leading-none"
              />
              <span className="text-caption text-foreground-secondary leading-none mt-0.5">
                {tTable('backersNoun', { count: proposal.backerCount })}
              </span>
            </div>
            <BackerAvatarPile backers={topBackers} />
          </div>
        </div>

        <div className={cn('h-[0.5px] w-full bg-border-primary my-1')} aria-hidden="true" />
        <div
          className="flex items-center justify-between gap-3 min-w-0"
          data-testid={footerTestId(variant)}
        >
          <div className="relative z-10 min-w-0 flex-1">
            <ProposalOutcomeCount
              proposalPubkey={proposal.pubkey}
              category={proposal.category}
              outcomes={proposal.outcomes}
              {...(eventVariant ? { categoryIcon: eventVariant.categoryIcon } : {})}
            />
          </div>
          {showVoting ? (
            <ReactionVoteButtons
              reaction={voteModel.reaction}
              upCount={voteModel.upCount}
              downCount={voteModel.downCount}
              upAriaLabel={voteModel.upAriaLabel}
              downAriaLabel={voteModel.downAriaLabel}
              onReact={voteModel.onReact}
              disabled={variant === 'compact'}
              className={cn(
                'relative z-10 shrink-0',
                variant === 'compact' && 'pointer-events-none',
              )}
            />
          ) : null}
        </div>
      </div>
    </IdentityBeam>
  );
}
